
import { useMemo, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'

import { COMPANY_USER_STATUSES } from 'constants/statuses'
import { date } from 'utils/date'
import { useCurrentCompany, useReferrerState } from 'hooks'
import { useSnackbar } from 'contexts'
import { useUpdateMemberProfile } from 'hooks/api/useUpdateMemberProfile'
import { useInviteCompanyAdmins } from 'hooks/api'
import { TrashIcon, UserPlusIcon, PencilIcon, ArchiveIcon, RotateCcwIcon, MailIcon } from 'lucide-react'
import { ROUTES } from 'routes'

import {
  Stack,
  Divider,
  Typography,
  Box,
} from '@mui/material'

import { NARROW_COLUMN_WIDTH } from 'config'
import { Button, Input, Card, Combobox } from '@repo/ui'
import {
  Loader,
  Header,
  EmptyState,
  InfoRow,
  InfoItem,
  DeleteCompanyUserModal,
  ArchiveCompanyUserModal,
  FormSubmitButton,
} from 'components'

type EditFormData = {
  first_name: string
  last_name: string
  title: string
  department: string
  phone: string
}

// Phone: optional, but if present must look like a phone number — 7–20 chars of
// digits and the usual formatting glyphs (+, spaces, dashes, parens), with at
// least 7 digits.
const PHONE_RULE = {
  validate: (v?: string) => {
    if (!v || v.trim() === '') return true
    const ok = /^\+?[\d\s().-]{7,20}$/.test(v) && (v.match(/\d/g)?.length ?? 0) >= 7
    return ok || 'Enter a valid phone number'
  },
}

export const UserPage = () => {
  const navigate = useNavigate()
  const { id: userId } = useParams()
  const { open: notify } = useSnackbar()
  // Detail → detail: the derived label would be the Users list, not this user.
  const referrer = useReferrerState('User')

  const { company, members, membersLoading, refetchMembers } = useCurrentCompany({ includeMembers: true })

  const [isEditing, setIsEditing] = useState(false)
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [openArchiveModal, setOpenArchiveModal] = useState(false)

  const user = useMemo(
    () => members.find((m) => m.id === userId),
    [members, userId]
  )

  const { control, handleSubmit, reset, formState: { isSubmitting, errors } } = useForm<EditFormData>({
    mode: 'onChange',
    defaultValues: { first_name: '', last_name: '', title: '', department: '', phone: '' },
  })

  const updateProfile = useUpdateMemberProfile()

  // Send / resend invite (Pending = first send, Invited = resend). Same endpoint
  // as the list kebab's re-invite; WorkOS sends the email (env-gated locally).
  const { mutate: sendInvite, isPending: isSendingInvite } = useInviteCompanyAdmins({
    onSuccess: () => {
      notify({ message: 'Invite sent', severity: 'success' })
      refetchMembers()
    },
    onError: (error: any) => {
      notify({ message: error?.response?.data?.message || error?.message || 'Failed to send invite', severity: 'error' })
    },
  })

  const handleSendInvite = () => {
    if (!user?.email) {
      notify({ message: 'Cannot send invite: no email on this user', severity: 'error' })
      return
    }
    sendInvite({ emails: [user.email], roleId: user.role?.id || undefined })
  }

  // Existing departments across company members — feed the creatable combobox.
  const departmentOptions = useMemo(() => {
    const set = new Set<string>()
    members.forEach((m) => {
      const d = (m.department ?? '').trim()
      if (d) set.add(d)
    })
    return Array.from(set).sort((a, b) => a.localeCompare(b))
  }, [members])

  const statusValue = user?.invitation_status ?? (user?.active ? 'active' : 'inactive')
  const isArchived = user?.archived || user?.invitation_status === 'inactive'
  const fullName = user ? `${user.first_name} ${user.last_name}`.trim() : ''
  const initials = `${user?.first_name?.[0] ?? ''}${user?.last_name?.[0] ?? ''}`.toUpperCase()

  const handleEditClick = () => {
    reset({
      first_name: user?.first_name ?? '',
      last_name: user?.last_name ?? '',
      title: user?.title ?? '',
      department: user?.department ?? '',
      phone: user?.phone ?? '',
    })
    setIsEditing(true)
  }

  const handleCancel = () => setIsEditing(false)

  const handleSave = handleSubmit(async (data) => {
    if (!company?.id || !userId) return
    try {
      await updateProfile.mutateAsync({ companyId: company.id, userId, data })
      await refetchMembers()
      setIsEditing(false)
      notify({ message: 'Profile updated', severity: 'success' })
    } catch {
      notify({ message: 'Failed to update profile', severity: 'error' })
    }
  })

  if (!membersLoading && !user) {
    return (
      <Stack>
        <EmptyState
          title="User not found"
          text="Return to the user directory to select a different member."
          buttons={
            <Button variant="primary" onClick={() => navigate(ROUTES.SETTINGS_COMPANY_USERS)}>
              Back to Users
            </Button>
          }
        />
      </Stack>
    )
  }

  return (
    /*
      Readable-measure cap, same one the settings sections opt into via `narrow` and the same
      constant. This page is a standalone route (App.tsx), so the settings shell's flag never
      reached it — the flag governs sections rendered INSIDE the shell, not detail pages that
      hang off them.

      It qualifies on the rule's own test: this is a form and a list of labelled switches, not
      a data table. Both cards are `flex: 1`, so they take whatever the window gives — nothing
      here has an intrinsic width to stop them.

      Measured at a 1920 viewport (content area 1840, sidebar minimised), capped vs uncapped
      under otherwise identical conditions:

        each card             884px → 538px
        Permissions row       640px → 294px between a label and the switch it controls

      640px of empty space between "Company Details" and its toggle is the failure: the control
      is nowhere near the thing it toggles, and the eye has to traverse the width of a laptop
      screen to pair them. 294px is what RolePage's fixed 452px rail already produces for the
      same kind of row, so this brings the two role/permission surfaces into agreement rather
      than inventing a measure. (With the sidebar expanded — content area 1664 — the same
      numbers were 796px cards and a 552px gap.)

      At 1280 the cap is close to inert: the uncapped column is 1152 and the cards 564px, so it
      trims 26px per card. As with the settings sections, this is a wide-window rule.

      The cap wraps the whole page, header included (WhatsNewPage's note: a centred column that
      leaves its title at the page's left edge reads as two unrelated things). The modals below
      are portaled, so being inside the wrapper does not constrain them.
    */
    <Stack sx={{ maxWidth: NARROW_COLUMN_WIDTH, mx: 'auto', width: '100%' }}>
      {membersLoading ? (
        <Loader />
      ) : (
        <Stack>
          <Header
            avatar={{ url: user?.avatar || '', letters: initials }}
            title={fullName || user?.email}
            status={{ statuses: COMPANY_USER_STATUSES, value: statusValue }}
            breadcrumbsOveride={fullName || user?.email || ''}
            rightComponents={
              <Stack direction="row" spacing={4} alignItems="center">
                {/* Pending = invite not yet sent → first send. Invited = already
                    sent, awaiting acceptance → resend. Parity with the list kebab. */}
                {user?.invitation_status === 'pending' && (
                  <>
                    <Button
                      disableElevation
                      loading={isSendingInvite}
                      startIcon={<UserPlusIcon strokeWidth={1} />}
                      variant="primary"
                      onClick={handleSendInvite}>
                      Send Invite
                    </Button>
                    <Divider orientation="vertical" flexItem />
                  </>
                )}
                {user?.invitation_status === 'invited' && (
                  <>
                    <Button
                      disableElevation
                      loading={isSendingInvite}
                      startIcon={<MailIcon strokeWidth={1} />}
                      variant="secondary"
                      onClick={handleSendInvite}>
                      Resend Invite
                    </Button>
                    <Divider orientation="vertical" flexItem />
                  </>
                )}
                {/* Archive (soft) / Reactivate, in parity with the list kebab */}
                {isArchived ? (
                  <Button
                    disableElevation
                    startIcon={<RotateCcwIcon strokeWidth={1} />}
                    variant="secondary"
                    onClick={() => setOpenArchiveModal(true)}>
                    Reactivate
                  </Button>
                ) : (
                  <Button
                    disableElevation
                    startIcon={<ArchiveIcon strokeWidth={1} />}
                    variant="secondary"
                    onClick={() => setOpenArchiveModal(true)}>
                    Archive
                  </Button>
                )}
                <Button
                  disableElevation
                  destructive="hover"
                  startIcon={<TrashIcon strokeWidth={1} />}
                  variant="secondary"
                  onClick={() => setOpenDeleteModal(true)}>
                  Delete
                </Button>
              </Stack>
            }
          />

          {/* Split layout: Details (left) + Permissions (right) — Figma 12890-314750 */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={6} alignItems="flex-start">
            {/* ---------- Details card ---------- */}
            <Box sx={{ flex: 1, width: '100%' }}>
              <Card>
                {/* minHeight = Edit button height (40px) so the header row keeps the
                    same height in edit mode (button hidden) and the title doesn't shift. */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4} sx={{ minHeight: 40 }}>
                  <Typography variant="body1" sx={{ fontSize: '20px' }}>Details</Typography>
                  {user?.invitation_status === 'active' && !isEditing && (
                    <Button
                      variant="secondary"
                      startIcon={<PencilIcon strokeWidth={1} size={18} />}
                      onClick={handleEditClick}
                      aria-label="Edit details"
                    >
                      Edit
                    </Button>
                  )}
                </Stack>

                {isEditing ? (
                  // pt matches the read-mode InfoItem top padding so the gap from
                  // the "Details" header to the first field is identical in both modes.
                  <Stack spacing={4} sx={{ pt: '20px' }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4}>
                      <Box sx={{ width: '100%' }}>
                        <Typography variant="body2" color="text.secondary" pb={2}>First name</Typography>
                        <Controller name="first_name" control={control}
                          render={({ field }) => <Input {...field} placeholder="First name" fullWidth />} />
                      </Box>
                      <Box sx={{ width: '100%' }}>
                        <Typography variant="body2" color="text.secondary" pb={2}>Last name</Typography>
                        <Controller name="last_name" control={control}
                          render={({ field }) => <Input {...field} placeholder="Last name" fullWidth />} />
                      </Box>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4}>
                      <Box sx={{ width: '100%' }}>
                        <Typography variant="body2" color="text.secondary" pb={2}>Title</Typography>
                        <Controller name="title" control={control}
                          render={({ field }) => <Input {...field} placeholder="Job title" fullWidth />} />
                      </Box>
                      <Box sx={{ width: '100%' }}>
                        <Typography variant="body2" color="text.secondary" pb={2}>Department</Typography>
                        <Controller name="department" control={control}
                          render={({ field }) => (
                            <Combobox
                              value={field.value}
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                              options={departmentOptions}
                              placeholder="Select or add a department"
                              fullWidth
                            />
                          )} />
                      </Box>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4}>
                      <Box sx={{ width: '100%' }}>
                        <Typography variant="body2" color="text.secondary" pb={2}>Phone</Typography>
                        <Controller name="phone" control={control} rules={PHONE_RULE}
                          render={({ field }) => (
                            <Input
                              {...field}
                              placeholder="Phone number"
                              fullWidth
                              error={!!errors.phone}
                              helperText={errors.phone?.message as string | undefined}
                            />
                          )} />
                      </Box>
                      <Box sx={{ width: '100%' }}>
                        <Typography variant="body2" color="text.secondary" pb={2}>Email</Typography>
                        <Typography variant="body2" color="text.disabled">{user?.email ?? '—'}</Typography>
                        <Typography variant="caption" color="text.disabled">Managed by SSO</Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={3} justifyContent="flex-end" pt={2}>
                      {/* Cancel stays enabled always — on a form with no separate
                          route to leave, this IS the exit affordance, not a discard. */}
                      <Button variant="secondary" onClick={handleCancel} disabled={isSubmitting}>Cancel</Button>
                      {/* Disabled until dirty + disabled on a field error (VRG-364) —
                          `reset()` in handleEditClick reseeds the dirty baseline to the
                          record's current values each time edit mode is entered. */}
                      <FormSubmitButton
                        control={control}
                        variant="primary"
                        onClick={handleSave}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Saving…' : 'Save'}
                      </FormSubmitButton>
                    </Stack>
                  </Stack>
                ) : (
                  <Stack>
                    <InfoRow>
                      <InfoItem spacing={false} label="Full name" value={fullName || '—'} />
                      <InfoItem
                        spacing={false}
                        label="Role"
                        value={
                          user?.role ? (
                            <Link to={ROUTES.SETTINGS_COMPANY_ROLE((user.role.id || user.role.name).toLowerCase())} state={referrer}>
                              {user.role.name}
                            </Link>
                          ) : '—'
                        }
                      />
                    </InfoRow>
                    <Divider />
                    <InfoRow>
                      <InfoItem spacing={false} label="Title" value={user?.title ?? '—'} />
                      <InfoItem spacing={false} label="Department" value={user?.department ?? '—'} />
                    </InfoRow>
                    <Divider />
                    <InfoRow>
                      <InfoItem spacing={false} label="Email" value={user?.email ?? '—'} />
                      <InfoItem spacing={false} label="Phone" value={user?.phone ?? '—'} />
                    </InfoRow>
                    <Divider />
                    <InfoRow>
                      <InfoItem spacing={false} label="Registration date" value={user?.created_at ? date(user.created_at) : '—'} />
                    </InfoRow>
                  </Stack>
                )}
              </Card>
            </Box>

            {/* ---------- Permissions card ---------- */}
            {/*
              VRG-422: this card used to render the VERA-816 Figma scaffold — four sections
              of switches labelled with the literal string 'Label', checked by ARRAY INDEX
              (`i % 2 === 0`), above a permanently disabled Edit. Nothing read the member's
              role, so it was not an empty placeholder a reader could discount: it was a
              confident wrong answer about someone's access, and roughly half the toggles
              were on for no reason at all.

              The capability shipped elsewhere. Real per-role permission management is
              /settings/roles (VERA-780), and permissions are a property of the ROLE, not of
              the person — so a per-user grid was always going to duplicate a surface that
              already exists. This points at it instead. Wiring the grid to the
              override-resolved `profile.role.permissions` remains possible later; removal is
              the smaller change and stops the surface lying in the meantime.
            */}
            <Box sx={{ flex: 1, width: '100%' }}>
              <Card>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4} sx={{ minHeight: 32 }}>
                  <Typography variant="body1" sx={{ fontSize: '20px' }}>Permissions</Typography>
                  {/* §42: carries the referrer so the role page can offer a way back to
                      this user, which the breadcrumb (Users → this user) cannot describe. */}
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() =>
                      navigate(
                        user?.role
                          ? ROUTES.SETTINGS_COMPANY_ROLE((user.role.id || user.role.name).toLowerCase())
                          : ROUTES.SETTINGS_COMPANY_ROLES,
                        { state: referrer },
                      )
                    }
                  >
                    {user?.role ? 'View role' : 'View roles'}
                  </Button>
                </Stack>

                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                  {user?.role ? (
                    <>
                      Access is set by role, not per person. The <strong>{user.role.name}</strong> role
                      decides what this user can see and do, and editing it changes access for everyone
                      assigned to it.
                    </>
                  ) : (
                    'Access is set by role, not per person. This user has no role yet, so they have no permissions. Assign one from Roles & Permissions.'
                  )}
                </Typography>
              </Card>
            </Box>
          </Stack>
        </Stack>
      )}

      <DeleteCompanyUserModal
        user={user}
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        refetch={refetchMembers}
      />
      <ArchiveCompanyUserModal
        user={user}
        open={openArchiveModal}
        onClose={() => setOpenArchiveModal(false)}
        refetch={refetchMembers}
        reactivate={isArchived}
      />
    </Stack>
  )
}
